/** ISO 4217 "no currency" — amounts render as plain numbers, without a symbol. */
export const NO_CURRENCY = "XXX";

/**
 * ISO 4217 zero-decimal currencies: their minor unit IS the major unit
 * (¥5000 is stored as 5000, not 500000). Payment providers (Stripe, Paddle)
 * use the same convention, so amounts stay interchangeable.
 */
const ZERO_DECIMAL_CURRENCIES = new Set(["JPY", "KRW", "VND", "CLP", "ISK", "UGX", "RWF"]);

/** 1 for zero-decimal currencies, 100 otherwise. */
function minorUnitFactor(currency: string): number {
  return ZERO_DECIMAL_CURRENCIES.has(currency) ? 1 : 100;
}

/** ISO minor units → a major-unit input string ("5000"+EUR → "50.00", "5000"+JPY → "5000"). */
export function minorUnitsToInputString(amountMinorUnits: number, currency: string): string {
  const factor = minorUnitFactor(currency);
  return (amountMinorUnits / factor).toFixed(factor === 1 ? 0 : 2);
}

/**
 * Format ISO minor units as a localized currency string, e.g. 5000 + EUR → "€50.00" (en-US) /
 * "50,00 €" (de-DE), 5000 + JPY → "￥5,000" (ja-JP). `locale` is required — pass the active UI
 * language (`getDisplayLocale()` or `i18n.language`); there is no silent default.
 */
export function formatMoney(amountMinorUnits: number, currency: string, locale: string): string {
  const factor = minorUnitFactor(currency);
  if (!currency || currency === NO_CURRENCY) {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amountMinorUnits / 100);
  }
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    }).format(amountMinorUnits / factor);
  } catch {
    return `${(amountMinorUnits / factor).toFixed(factor === 1 ? 0 : 2)} ${currency}`;
  }
}

/**
 * Parse a user-entered major-unit amount ("50" / "50.00") into ISO minor units
 * for the given currency; NaN if invalid. Defaults to cent-style currencies
 * when no currency is known.
 */
export function parseMoneyToMinorUnits(input: string, currency: string = NO_CURRENCY): number {
  const value = Number(input.replace(",", "."));
  if (Number.isNaN(value) || value < 0) return NaN;
  return Math.round(value * minorUnitFactor(currency));
}

/**
 * Currencies offered in pickers: the ten most traded world currencies, the
 * pre-existing PLN/UAH, and NO_CURRENCY for everyone whose currency isn't listed.
 * The organization's choice is preselected.
 */
export const SUPPORTED_CURRENCIES = [
  "USD", "EUR", "GBP", "JPY", "CNY", "AUD", "CAD", "CHF", "HKD", "SGD",
  "PLN", "UAH", NO_CURRENCY,
];

/** "USD" → "$", "EUR" → "€" … falls back to the code itself; empty for no-currency. */
export function currencySymbol(currency: string): string {
  if (!currency || currency === NO_CURRENCY) return "";
  try {
    const symbol = (0)
      .toLocaleString(undefined, { style: "currency", currency, minimumFractionDigits: 0 })
      .replace(/[\d.,\s]/g, "");
    return symbol || currency;
  } catch {
    return currency;
  }
}
